'use client';
import { useEffect } from 'react';
import { useCookieConsent } from '@/components/hook/cookie-consent';

type BrazeInitProps = {
  brazeLogging?: boolean;
  className?: string;
  /** Optional user id to identify the Braze user before opening a session */
  userId?: string;
};

declare global {
  interface Window {
    braze?: typeof import('@braze/web-sdk');
    brazePendingProductViewed?: string;
  }
}

const brazeApiBaseUrl = process.env.NEXT_PUBLIC_BRAZE_SDK_BASE_URL || '';
const brazeApiKey = process.env.NEXT_PUBLIC_BRAZE_SDK_API_KEY || '';

function BrazeInit(props: BrazeInitProps) {
  const { brazeLogging = false, userId } = props;
  const consent = useCookieConsent();

  // https://www.braze.com/docs/developer_guide/platform_integration_guides/web/initial_sdk_setup/?tab=before%20initialization#ssr
  // "If you use a server-side rendering framework such as Next.js,
  // you may encounter errors because the SDK is meant to be run in a
  // browser environment. You can resolve these issues by dynamically
  // importing the SDK.
  // You can retain the benefits of tree-shaking when doing so by exporting
  // the parts of the SDK that you need in a separate file and then
  // dynamically importing that file into your component."
  useEffect(() => {
    if (!consent.functional) return;

    import('./braze-exports').then((brazeModule) => {
      const braze = brazeModule.default;

      // Attach braze to window for global access BEFORE initialization
      // This ensures that any code checking for window.braze will find it
      (window as any).braze = braze;

      // Check if Braze is already initialized (by GTM or another script)
      // Use the new isInitialized() method available in v6+
      if (braze.isInitialized()) {
        console.warn(
          '[BrazeInit] Braze SDK already initialized. Skipping React initialization to prevent conflicts.'
        );
        return;
      }

      // Initialize the SDK
      braze.initialize(brazeApiKey, {
        baseUrl: brazeApiBaseUrl,
        enableLogging: brazeLogging,
        sessionTimeoutInSeconds: 900,
        doNotLoadFontAwesome: true,
        manageServiceWorkerExternally: true,
        disablePushTokenMaintenance: true,
        noCookies: true
      });

      // Automatically show in-app messages (matches GTM implementation)
      braze.automaticallyShowInAppMessages();

      if (userId) {
        // Set the Braze user before opening the session
        braze.changeUser(userId);
      }

      braze.openSession();

      // Set by the PDP when it loaded before Braze was ready
      if (window.brazePendingProductViewed) {
        braze
          .getUser()
          ?.setCustomUserAttribute(
            'last_product_viewed',
            window.brazePendingProductViewed
          );
        window.brazePendingProductViewed = undefined;
      }
    });
  }, [consent.functional, brazeLogging, userId]);

  // Render nothing - this component is only included so that the init code
  // above will run client-side
  return null;
}

export default BrazeInit;
